import React from "react"
import { Slider, styled, Typography } from "@material-ui/core"
import classNames from "classnames"
import { createStyles, makeStyles } from "@material-ui/core/styles"
import { theme } from "../../../layouts/MainFrame"
import sliderIcon from "../../../assets/images/icon/slider_icon.png"
import { BusinessType, RecommendedPricing } from "../../../types"
import { FormattedMessage } from "react-intl"
import { Locale } from "../../../locale"

const useStyles = makeStyles(() =>
  createStyles({
    chargeSelector: {
      padding: "27px 32px",
      background: "#fff",
      width: "710px",
      border: "1px solid #ededed",
      borderRight: "none",
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "0",
      borderBottomLeftRadius: "20px",
      borderBottomRightRadius: "0",
      [theme.breakpoints.down("sm")]: {
        padding: "24px 26px",
        width: "100%",
        height: "222px",
        border: "1px solid #ededed",
        borderBottom: "none",
        borderTopLeftRadius: "20px",
        borderTopRightRadius: "20px",
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
      },
    },
    categoryContainer: {
      margin: "13px 0 47px 0",
      padding: 0,
      listStyle: "none",
      display: "flex",
      border: "1px solid #ededed",
      borderRadius: "4px",
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: {
        margin: "13px 0 25px 0",
      },
    },
    categoryItem: {
      height: "48px",
      flex: "25%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#ffffff",
      fontWeight: 400,
      color: "rgba(0,0,0,0.6)",
      wordBreak: "keep-all",
      textAlign: "center",
      cursor: "pointer",
      fontSize: "18px",
      "&:not(:last-child)": {
        borderRight: "1px solid #ededed",
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "12px",
        padding: "9px",
      },
    },
    marker: {
      display: "flex",
      justifyContent: "space-between",
      "& div": {
        fontSize: "14px",
        color: "rgba(0, 0, 0, 0.6)",
      },
    },
    active: {
      fontWeight: 700,
      background: "#E8EFFC",
      color: "#2962FF",
    },
    typo: {
      fontSize: "18px",
      color: "rgba(0, 0, 0, 0.87)",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
        color: "#4B4B4B",
      },
    },
    sliderLabel: {
      width: "184px",
      height: "82px",
      padding: "4px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      rowGap: "4px",
      fontSize: "14px",
      borderRadius: "4px",
      "& strong": {
        fontWeight: 700,
        fontSize: "16px",
      },
      "&::before": {
        content: "''",
        position: "absolute",
        display: "block",
        width: "0px",
        left: "50%",
        top: "-13px",
        border: "12px solid transparent",
        borderTop: "0",
        borderBottom: "15px solid #051A52",
        transform: "translate(-50%, calc(-100% - 5px))",
      },
      [theme.breakpoints.down("sm")]: {
        width: "134px",
        height: "52px",
        fontSize: "10px",
        rowGap: "0",
        "& strong": {
          fontSize: "14px",
        },
        "&::before": {
          content: "''",
          position: "absolute",
          display: "block",
          width: "0px",
          left: "50%",
          top: "0px",
          border: "12px solid transparent",
          borderTop: "0",
          borderBottom: "15px solid #051A52",
          transform: "translate(-50%, calc(-100% - 5px))",
        },
      },
    },
  })
)

interface CalculationControllerProps {
  visitor: number
  categories: { type: BusinessType; text: string }[]
  categoryIndex: number
  setCategoryIndex: (index: number) => void
  onChange: (value: number) => void
  recommendedPricing: RecommendedPricing
}

export default function CalculationController({
  categories,
  categoryIndex,
  setCategoryIndex,
  visitor,
  onChange,
  recommendedPricing,
}: CalculationControllerProps) {
  const classes = useStyles()
  const lang = Locale.getLangCode()
  const { monthlyExpectedEvent } = recommendedPricing
  const { minimum, maximum } = monthlyExpectedEvent

  const monthlyPrice = () => {
    if (lang === "ko") {
      return `${minimum / 10000}~${maximum / 10000}만건/월`
    }
    return `${minimum / 1000000}~${maximum / 1000000}M events/mo`
  }
  const visitorText = (value: number) => {
    if (lang === "ko") {
      return `${value / 10000}만명`
    }
    if (value === 1000000) {
      return `1M`
    }
    return `${value / 1000}K`
  }
  return (
    <div className={classes.chargeSelector}>
      <div>
        <div>
          <Typography className={classes.typo}>
            <FormattedMessage id={"pricing_calculator_category"} />
          </Typography>
        </div>
        <ul className={classes.categoryContainer}>
          {categories.map((category, index) => {
            return (
              <li
                key={index}
                className={classNames([classes.categoryItem, categoryIndex === index && classes.active])}
                onClick={() => setCategoryIndex(index)}>
                {category.text}
              </li>
            )
          })}
        </ul>
      </div>
      <div>
        <div>
          <Typography className={classes.typo}>
            <FormattedMessage id={"pricing_calculator_mau"} />
          </Typography>
        </div>
        <div>
          <CalculatorSlider
            onChange={(_, value) => {
              if (typeof value === "number") onChange(value)
            }}
            valueLabelFormat={(value) => {
              return (
                <div>
                  <div className={classes.sliderLabel}>
                    <div>
                      <strong>{visitorText(value)}</strong>
                    </div>
                    <div>
                      <FormattedMessage id={"pricing_calculator_expected_usage"} />
                    </div>
                    <div>{monthlyPrice()}</div>
                  </div>
                </div>
              )
            }}
            valueLabelDisplay={"on"}
            value={visitor}
            max={1000000}
            step={10000}
            marks={[
              {
                value: 0,
                label: "",
              },
              {
                value: 200000,
                label: "",
              },
              {
                value: 400000,
                label: "",
              },
              {
                value: 600000,
                label: "",
              },
              {
                value: 800000,
                label: "",
              },
              {
                value: 1000000,
                label: "",
              },
            ]}
          />
          <div className={classes.marker}>
            <div>{lang === "ko" ? "0명" : "0"}</div>
            <div>{lang === "ko" ? "100만명" : "1M"}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CalculatorSlider = styled(Slider)(() => ({
  "@media (pointer: coarse)": {
    padding: "16px 0 5px",
  },
  "& .MuiSlider-root": {
    padding: "18px 0 5px",
  },
  "& .MuiSlider-rail": {
    height: "8px",
    color: "#C4C4C4",
    opacity: 1,
  },
  "& .MuiSlider-track": {
    height: "8px",
  },
  "& .MuiSlider-thumb": {
    height: 20,
    width: 20,
    backgroundColor: "#fff",
    backgroundImage: `url(${sliderIcon})`,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#fff",
    height: 8,
    width: 2,
  },
  "& .MuiSlider-valueLabel": {
    left: "-7px",
    top: "68px",
    "& span": {
      background: "#051A52",
      transform: "none",
      borderRadius: "4px",
    },
    [theme.breakpoints.down("sm")]: {
      left: "-7px",
      top: "46px",
    },
  },
}))
